import { useGetAllAssetInsights } from "@/api/studio/insights/queries";
import { MemoryIcon, PadlockIcon } from "@/assets/icons";
import { ReportsLayout } from "@/components/layouts";
import { Card, CardBody, CardSkeleton } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { EntitlementGate } from "@/features/auth/components/entitlement-gate";
import { Metric } from "@/features/reports/components/metric";
import { ReportBarChart } from "@/features/reports/components/report-bar-chart";
import { ReportNavToolbar } from "@/features/reports/components/report-nav-toolbar";
import { useReportsContext } from "@/features/reports/contexts/reports-context";
import { useReportFiltersStore } from "@/features/reports/stores/use-report-filters-store";
import { getThumbnailImage } from "@/features/reports/utils/get-thumbnail-image";
import { getTopScore } from "@/features/reports/utils/get-top-score";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { EyeIcon, HeartIcon } from "@heroicons/react/20/solid";
import { useEffect } from "react";
import { useLocation } from "wouter";

/* Prop Types */
interface ReportsCreativeOverviewRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * ReportsCreativeOverviewRoute component
 *
 * This provides a UI to show an overview of the creative report for a specific study.
 */
export const ReportsCreativeOverviewRoute = ({ projectId, studyId }: ReportsCreativeOverviewRouteProps) => {
  /* Stores */
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const orderBy = useReportFiltersStore((state) => state.orderBy);
  const theme = useGlobalStore((state) => state.theme);
  const [, navigate] = useLocation();

  /* Context */
  const {
    state: { view },
    actions: { setReportType, setAssetKey },
  } = useReportsContext();

  /* Queries */
  const { data: allInsights, isLoading: isAllInsightsLoading } = useGetAllAssetInsights({ projectId, studyId, orderBy, sortBy });
  /* Hooks */
  const { segmentTrackEvent, segmentPageEvent } = useSegment();
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "reports/overview");

  /* Handlers */
  const handleClick = (assetId: string) => {
    // segment track event when a user clicks on a report asset
    segmentTrackEvent("Asset Report Viewed", {
      studyId,
      projectId,
      assetId,
      reportType: "creative",
      studyType: "asset",
    });
    // set the report type and assetKey
    setReportType("creative");
    setAssetKey(assetId);
    // navigate to the in-depth report page for the selected asset
    navigate(`/projects/${projectId}/studies/${studyId}/asset/reports/in-depth`);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the reports overview page
    if (theme)
      segmentPageEvent("Study Report", {
        title: "Creative Overview Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId}>
      {/* Navbar */}
      <ReportNavToolbar projectId={projectId} studyId={studyId} currentTab="creative" hasContext={false} />

      {/* Body */}
      <div className="grow flex flex-row gap-4 overflow-hidden">
        <div className="grow flex flex-col gap-4 overflow-y-auto">
          {/* Cards */}
          {view === "Cards" && (
            <div className="flex flex-row gap-4 flex-wrap">
              {/* Loading */}
              {isAllInsightsLoading && Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} />)}

              {/* Insights */}
              {allInsights &&
                allInsights.map((insight, index) => (
                  <Card
                    key={insight.key}
                    onClick={() => handleClick(insight.key)}
                    imageUrl={getThumbnailImage(insight.key, 300, 300, projectId, studyId)}
                    imgFit="contain"
                    winner={getTopScore(index, allInsights.length, sortBy)}
                  >
                    <CardBody className="flex flex-col gap-5 px-4 py-5">
                      <div className="flex flex-col gap-2 truncate">
                        <p className="text-xs truncate">{insight.assetName}</p>
                        <Metric title="Creative Impact" className="text-base font-semibold" value={Math.round(insight?.metrics?.creative?.score || 0)} />
                      </div>

                      <div className="flex flex-col gap-3">
                        <Metric title="Attention" size="small" value={insight.metrics.attention.score} icon={<EyeIcon className="size-4" />} />
                        <EntitlementGate
                          entitlement="memoryEmotion"
                          fallback={<Metric title="Emotion" size="small" value={0} className="opacity-60" icon={<HeartIcon className="size-4 opacity-60" />} locked />}
                        >
                          <Metric title="Emotion" size="small" value={Math.round(insight?.metrics?.emotion?.score || 0)} icon={<HeartIcon className="size-4" />} />
                        </EntitlementGate>
                        <EntitlementGate
                          entitlement="memoryEmotion"
                          fallback={<Metric title="Memory" size="small" value={0} className="opacity-60" icon={<MemoryIcon className="size-4 opacity-60" />} locked />}
                        >
                          <Metric title="Memory" size="small" value={Math.round(insight?.metrics?.memory?.score || 0)} icon={<MemoryIcon className="size-4" />} />
                        </EntitlementGate>
                      </div>
                    </CardBody>
                  </Card>
                ))}
            </div>
          )}

          {/* Chart */}
          {view === "Chart" && (
            <>
              <div className="max-h-80 min-h-80 h-80 grow border-b border-base-300">
                {allInsights && <ReportBarChart insightData={allInsights} projectId={projectId} studyId={studyId} variant="creative" />}
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeader>Assets</TableHeader>
                      <TableHeader className="w-40">Ranking</TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-primary"></span>
                          Creative
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-[rgb(25,211,200)]"></span>
                          Attention
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-[rgb(242,153,74)]"></span>
                          Emotion
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-accent"></span>
                          Memory
                        </div>
                      </TableHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {allInsights &&
                      allInsights.map((insight, index) => (
                        <TableRow key={insight.key}>
                          <TableCell className="truncate">
                            <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                            <small className="truncate">{insight.assetName}</small>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-row items-center gap-2">
                              {sortBy === "asc" ? allInsights.length - index : index + 1}
                              {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                            </div>
                          </TableCell>
                          <TableCell>{Math.round(insight?.metrics?.creative?.score || 0)}%</TableCell>
                          <TableCell>{insight.metrics.attention.score}%</TableCell>
                          <TableCell>
                            <EntitlementGate
                              entitlement="memoryEmotion"
                              fallback={
                                <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient />
                                </div>
                              }
                            >
                              {Math.round(insight?.metrics?.emotion?.score || 0)}%
                            </EntitlementGate>
                          </TableCell>
                          <TableCell>
                            <EntitlementGate
                              entitlement="memoryEmotion"
                              fallback={
                                <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient />
                                </div>
                              }
                            >
                              {Math.round(insight?.metrics?.memory?.score || 0)}%
                            </EntitlementGate>
                          </TableCell>
                        </TableRow>
                      ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {/* List */}
          {view === "List" && allInsights && (
            <div className="flex-1 flex flex-col gap-4">
              <Table>
                <thead>
                  <TableRow>
                    <TableHeader>Assets</TableHeader>
                    <TableHeader className="w-40">Ranking</TableHeader>
                    <TableHeader className="w-56">Creative</TableHeader>
                    <TableHeader className="w-56">Attention</TableHeader>
                    <TableHeader className="w-56">Emotion</TableHeader>
                    <TableHeader className="w-56">Memory</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {allInsights.map((insight, index) => (
                    <TableRow key={insight.key} onClick={() => handleClick(insight.key)}>
                      <TableCell className="truncate">
                        <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                        <small className="truncate">{insight.assetName}</small>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-row items-center gap-2">
                          {sortBy === "asc" ? allInsights.length - index : index + 1}
                          {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Progress value={Math.round(insight?.metrics?.creative?.score || 0)} className="w-20" />
                        {Math.round(insight?.metrics?.creative?.score || 0)}%
                      </TableCell>
                      <TableCell>
                        <Progress value={insight.metrics.attention.score} className="w-20" />
                        {insight.metrics.attention.score}%
                      </TableCell>
                      <TableCell>
                        <EntitlementGate
                          entitlement="memoryEmotion"
                          fallback={
                            <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                              <PadlockIcon isGradient />
                            </div>
                          }
                        >
                          <Progress value={Math.round(insight?.metrics?.emotion?.score || 0)} className="w-20" />
                          {Math.round(insight?.metrics?.emotion?.score || 0)}%
                        </EntitlementGate>
                      </TableCell>
                      <TableCell>
                        <EntitlementGate
                          entitlement="memoryEmotion"
                          fallback={
                            <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                              <PadlockIcon isGradient />
                            </div>
                          }
                        >
                          <Progress value={Math.round(insight?.metrics?.memory?.score || 0)} className="w-20" />
                          {Math.round(insight?.metrics?.memory?.score || 0)}%
                        </EntitlementGate>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};
